/**
 * LivingMetabolismCard.jsx — iter926 (Feb 2026)
 *
 * "Living Metabolism" panel: patient marks recommendations as executed
 * today → weights update in the Pathway Vault → BHI recalculates.
 *
 * MVP surface:
 *   • Streak counter (🔥 N поредни дни)
 *   • 30-day timeline strip (green = executed, gray = idle, red = skipped)
 *   • Top-3 most-boosted pathways in last 30 days
 *   • "Отбележи изпълнено днес" quick-log demo button (bound to a
 *     generic recommendation_id; real integrations coming next).
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Flame, TrendingUp, Calendar, Loader2 } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { emitHealthEvent } from "@/utils/healthEvents";
import { toast } from "sonner";

export default function LivingMetabolismCard() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [logging, setLogging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/living-metabolism/summary`),
        axios.get(`${API}/living-metabolism/timeline?days=30`),
      ]);
      setSummary(s.data || null);
      setTimeline(t.data?.timeline || []);
    } catch (e) {
      // Silent — panel just stays empty
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const logDone = useCallback(async (recId, pathways, actionType = "executed") => {
    if (logging) return;
    setLogging(true);
    try {
      const { data } = await axios.post(`${API}/living-metabolism/log`, {
        recommendation_id: recId,
        action_type: actionType,
        pathways: pathways || [],
        source_module: "bhi",
      });
      if (data?.duplicate) {
        toast.info("Вече е отбелязано за днес.");
      } else {
        toast.success(actionType === "executed"
          ? "🌱 Отбелязано! Vault-a се обнови."
          : "Отбелязано като пропуснато.");
        emitHealthEvent("living-metabolism-update", { pathways });
      }
      await load();
    } catch (e) {
      toast.error("Грешка при записване.");
    } finally {
      setLogging(false);
    }
  }, [API, logging, load]);

  if (loading) {
    return (
      <div className={`p-4 rounded-2xl ${isDark ? "bg-white/[0.04]" : "bg-white"} border ${isDark ? "border-white/10" : "border-gray-200"}`}
           data-testid="living-metabolism-card-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
        <span className="text-sm">Зарежда се…</span>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-2xl ${isDark ? "bg-white/[0.04]" : "bg-white"} border ${isDark ? "border-white/10" : "border-gray-200"}`}
         data-testid="living-metabolism-card">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="text-lg">🌱</span>
          <span className="text-sm font-extrabold">Жив метаболизъм</span>
        </div>
        {summary && summary.streak_days > 0 && (
          <div className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-bold ${
            isDark ? "bg-orange-500/20 text-orange-300" : "bg-orange-100 text-orange-700"
          }`} data-testid="lm-streak-badge">
            <Flame className="w-3 h-3" />
            {summary.streak_days} поредни дни
          </div>
        )}
      </div>

      <p className="text-[11px] opacity-70 mb-3 leading-relaxed">
        Отбелязвайте всеки ден кои препоръки сте изпълнили. Всяко действие
        адаптира тежестите в KEGG пътищата на вашия Digital Twin и генерира
        нови персонализирани препоръки.
      </p>

      {/* Stats row */}
      {summary && (
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="lm-stats">
          <div className={`p-2 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <div className="text-[9px] opacity-60 uppercase font-bold">Изпълнени общо</div>
            <div className="text-lg font-extrabold">{summary.total_executions || 0}</div>
          </div>
          <div className={`p-2 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <div className="text-[9px] opacity-60 uppercase font-bold">Пропуснати</div>
            <div className="text-lg font-extrabold">{summary.total_skipped || 0}</div>
          </div>
          <div className={`p-2 rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
            <div className="text-[9px] opacity-60 uppercase font-bold">Streak</div>
            <div className="text-lg font-extrabold">{summary.streak_days || 0}</div>
          </div>
        </div>
      )}

      {/* Timeline strip */}
      {timeline.length > 0 && (
        <div className="mb-3" data-testid="lm-timeline">
          <div className="flex items-center gap-1 mb-1">
            <Calendar className="w-3 h-3 opacity-60" />
            <span className="text-[10px] font-bold uppercase opacity-70">Последни 30 дни</span>
          </div>
          <div className="flex gap-0.5">
            {timeline.map((d, i) => {
              const executed = d.executed_count || 0;
              const skipped = d.skipped_count || 0;
              let bg = isDark ? "bg-white/5" : "bg-gray-100";
              if (executed > 0) bg = executed >= 3 ? "bg-emerald-500" : "bg-emerald-400/70";
              else if (skipped > 0) bg = "bg-rose-400/60";
              return (
                <div key={d.ymd}
                     className={`h-6 flex-1 rounded-sm ${bg}`}
                     title={`${d.ymd}: изпълнени=${executed}, пропуснати=${skipped}`}
                     data-testid={`lm-day-${i}`}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Top boosted pathways */}
      {summary?.top_boosted_pathways?.length > 0 && (
        <div className="mb-3" data-testid="lm-top-pathways">
          <div className="flex items-center gap-1 mb-1">
            <TrendingUp className="w-3 h-3 opacity-60" />
            <span className="text-[10px] font-bold uppercase opacity-70">Топ boost-нати пътища (30 дни)</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {summary.top_boosted_pathways.map((p, i) => (
              <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded-md font-mono ${
                isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-800"
              }`}>
                {p.pathway_id} +{p.boost_count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Quick-log demo (bound to a generic "daily healthy meal" recommendation).
          Real integrations: each BTRE/BPP recommendation gets its own button next. */}
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => logDone("daily_veg_serving", ["gut_microbiome", "SCFA"], "executed")}
          disabled={logging}
          className={`text-[11px] px-3 py-1.5 rounded-lg font-bold ${
            isDark ? "bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30"
                   : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
          } disabled:opacity-50`}
          data-testid="lm-log-veg-btn"
        >
          {logging ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : "✅ "}
          Ядох 5+ порции зеленчуци днес
        </button>
        <button
          type="button"
          onClick={() => logDone("daily_omega3_serving", ["arachidonic_acid", "NF-kB", "IL6"], "executed")}
          disabled={logging}
          className={`text-[11px] px-3 py-1.5 rounded-lg font-bold ${
            isDark ? "bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"
                   : "bg-cyan-100 text-cyan-700 hover:bg-cyan-200"
          } disabled:opacity-50`}
          data-testid="lm-log-omega3-btn"
        >
          🐟 Приех Омега-3
        </button>
        <button
          type="button"
          onClick={() => logDone("daily_movement_30min", ["insulin_signaling", "AMPK"], "executed")}
          disabled={logging}
          className={`text-[11px] px-3 py-1.5 rounded-lg font-bold ${
            isDark ? "bg-fuchsia-500/20 text-fuchsia-300 hover:bg-fuchsia-500/30"
                   : "bg-fuchsia-100 text-fuchsia-700 hover:bg-fuchsia-200"
          } disabled:opacity-50`}
          data-testid="lm-log-move-btn"
        >
          🚶 30 мин движение
        </button>
      </div>
      <div className="text-[9px] opacity-50 italic mt-2">
        Всяко изпълнение добавя +2 към теглото на свързаните KEGG пътища
        във вашия Pathway Vault. Промените се отразяват в BHI и Auto-Diagnosis.
      </div>
    </div>
  );
}
